import React, { FC, Fragment, useMemo } from 'react';
import styled from '@emotion/styled';
import { lighten } from 'color2k';
import { IEnrichedProductSerie } from '$models';
import { useTranslation } from '$hooks';
import { key } from '~/utils';
import { Link } from '$components/elements/link';
import { Flex } from '$components/layouts';
import { Text } from '$components/elements/text';
import { Button } from '$components/elements/button';
import { mq } from '$lib/helpers';

type Props = {
    series?: IEnrichedProductSerie[];
    shortDescription?: string;
    description?: string;
    onReadMore?: () => void;
};

export const ProductMediaDescription: FC<Props> = ({
    series,
    shortDescription,
    description,
    onReadMore,
}: Props): JSX.Element => {
    const { translate } = useTranslation();

    const descriptionText = useMemo(() => {
        if (shortDescription) {
            return shortDescription;
        }
        if (!description) {
            return undefined;
        }
        if (description?.length > 200) {
            return `${description?.slice(0, 200)}...`;
        }
        return description;
    }, [description, shortDescription]);

    return (
        <ContentContainer column gap="small">
            {!!series?.length && (
                <StyledLinks>
                    {`${translate('productPage.productDetails.seriesPrefix')} `}
                    {series.map((serie: IEnrichedProductSerie, index: number) => (
                        <Fragment key={key(serie)}>
                            <Link themedLink href={serie?.url}>
                                {serie?.title}
                            </Link>
                            {index < (series?.length || 0) - 2 && <span>{`, `}</span>}
                            {index === (series?.length || 0) - 2 && (
                                <span>{` ${translate('person.details.and')} `}</span>
                            )}
                        </Fragment>
                    ))}
                </StyledLinks>
            )}
            {descriptionText && <ShortDescription>{descriptionText}</ShortDescription>}
            <ReadMore variant="inverted" size="default" onClick={onReadMore}>
                {translate('productPage.productDetails.readMore')}
            </ReadMore>
        </ContentContainer>
    );
};

const ContentContainer = styled(Flex)(({ theme }) => ({
    alignItems: 'center',
    padding: `0 ${theme.space[4]}`,
    [mq('frame')]: {
        alignItems: 'flex-start',
        padding: 0,
    },
}));

const StyledLinks = styled.div(({ theme }) => ({
    display: 'block',
    alignItems: 'center',
    fontSize: theme.fontSizes.md,
    fontFamily: theme.fontFamilies.regular,
    fontWeight: theme.fontWeights.normal,
    marginRight: 0,
    marginBottom: 0,
}));

const ShortDescription = styled(Text)({
    textAlign: 'center',
    [mq('frame')]: {
        textAlign: 'left',
    },
});

const ReadMore = styled(Button)(({ theme }) => ({
    padding: `${theme.space[2]} ${theme.space[6]}`,
    height: 'auto',
    [mq('frame')]: {
        minWidth: '140px',
    },
    '&:hover': {
        border: theme.general.border,
        borderColor: theme.colors.primary,
        backgroundColor: lighten(theme.colors.white, 0.05),
    },
}));
